/* eslint-disable no-console */
import React, { useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { get, upperCase } from 'lodash';
import includes from 'lodash/includes';
import { message, Spin, Input, Icon } from 'antd';
import pointInPolygon from 'point-in-polygon';
import circleToPolygon from 'circle-to-polygon';
import { httpGetAsync } from '$app/utils/util';
import { getAllGarages } from '$app/common/api';
import { CONSTANTS } from './constants';
import genericScale from '../../utils/genericScale';
import { getGarageZoneShapes, getDriverZoneShapes, createDriverZone, getCoveredOutbounds } from './actions';
import {
  Container,
  Header,
  MapView,
  MapMount,
  ToggleGroup,
  ToggleButton,
  MapFooter,
  ContainFormRight,
  NewZoneButton,
  DownloadOutboundsButton,
  PostcodeSearchContainer,
  LogoSpinner,
} from './styled';
import ZoneEditorForm from '../ZoneEditorForm';
import If from '../../components/Helpers/If';

import { api } from '$app/api';

const { Search } = Input;
const infowindow = new google.maps.InfoWindow();
let googleMap = null;

const CUSTOMER_CND = 'CustomerC&D';
const GARAGE_CND = 'GarageC&D';

const CoverageZoneMap = ({ history }) => {
  const [districts, setDistricts] = useState(null);
  const [toggledDistricts, toggleDistricts] = useState(false);
  const [marketingDistricts, setMarketingDistricts] = useState(null);
  const [toggledMarketingDistricts, toggleMarketingDistricts] = useState(false);
  const [garageShapes, setGarageShapes] = useState(null);
  const [garageLocations, setGarageLocations] = useState(null);
  const [driverShapes, setDriverShapes] = useState(null);
  const [garageFilters, setGarageFilters] = useState(['ACTIVE']);
  const [driverFilters, setDriverFilters] = useState(['ACTIVE']);
  const [cndTypeFilters, setCndTypeFilters] = useState([CUSTOMER_CND, GARAGE_CND]);
  const [showForm, setShowForm] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(11);
  const [mapCentre, setMapCentre] = useState({ lat: 51.505, lng: -0.09 });
  const [fetchingPostcode, setFetchingPostcode] = useState(null);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [loadOutbounds, setLoadOutbounds] = useState(false);

  const [customerRadiusMiles, setCustomerRadiusMiles] = useState(11);

  const mapRef = useRef();

  function mapInit() {
    googleMap = new google.maps.Map(mapRef.current, {
      center: mapCentre,
      zoom: zoomLevel,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      styles: genericScale,
    });
  }

  async function getGarageShape() {
    try {
      const shapes = await getGarageZoneShapes();
      const garages = await getAllGarages();

      const newGarageLocations = garages.map((g) => {
        const status = get(g, 'status').toUpperCase();
        const collectionType = get(g, 'deliveryOptions[0].deliveryOption');
        return {
          lat: g.latitude,
          lng: g.longitude,
          name: g.businessName,
          status,
          collectionType,
          garageId: g.garageId,
        };
      });

      setGarageLocations(newGarageLocations);

      const newGarageShapes = shapes.map((shape) => {
        shape.garage = garages.find((g) => g.garageId === shape.ownerId);
        return shape;
      });

      setGarageShapes(newGarageShapes);
    } catch (err) {
      throw Error(err);
    }
  }

  async function getDriverShape() {
    try {
      const shapes = await getDriverZoneShapes();

      setDriverShapes(shapes);
    } catch (err) {
      throw Error(err);
    }
  }

  useEffect(() => {
    async function getDistricts() {
      try {
        await httpGetAsync('/assets/Districts.json', (_districts) => {
          setDistricts(JSON.parse(_districts));
        });
      } catch (err) {
        throw Error(err);
      }
    }

    getGarageShape().then(() => console.log('Garage Shape set'));
    getDriverShape().then(() => console.log('Driver zone Shape set'));
    getDistricts().then(() => console.log('Districts set'));
  }, []);

  useEffect(() => {
    const allPolygons = [];

    const getZoneAttribtutes = (zone) => {
      const status = get(zone, 'garage.status', zone.status || '').toUpperCase();

      if (!status) {
        console.log('Area with no status', zone);
      }

      return {
        type: zone.ownerType,
        status,
        collectionType: get(zone, 'garage.deliveryOptions[0].deliveryOption', null),
        businessName: get(zone, 'garage.businessName', zone.name),
        garageId: get(zone, 'garage.garageId', zone.ownerId),
      };
    };

    function getFeatureGeo(zone) {
      const { type, status, collectionType, businessName, garageId } = getZoneAttribtutes(zone);
      return {
        type: 'Feature',
        ...zone.geoJson,
        properties: {
          name: businessName,
          garageId,
          status,
          type,
          collectionType,
        },
      };
    }

    function createCircleGeodata(zone) {
      const { longitude, latitude } = zone.garage;
      const customerRadiusInMetres = customerRadiusMiles * 1.6 * 1000;
      return circleToPolygon(
        [longitude || 0, latitude || 0],
        customerRadiusInMetres,
        32 // number of edges
      );
    }

    function polygonInfo(garage) {
      const { type, garageId, name, status, collectionType } = garage;
      const url = type === 'garage' ? `/garages/${garageId}/coverage` : `/coverage-zones/${garageId}`;
      return `
        <h4>
          <a target="_blank" href="${url}">${name}</a>
        </h4>
        <small>${status}</small>
        <br/>
        <small>
          ${upperCase(type)}
          ${type === 'driver' ? ' ZONE' : ''}
        </small>
        <br/>
        <small>
          ${collectionType ? `C&D Type: ${collectionType}` : ''}
        </small>
        <hr>
      `;
    }

    function polygonsInfo(polygons) {
      return polygons.reduce((html, polygon) => html + polygonInfo(polygon.properties), '');
    }

    function polygonIsIntersecting(location, polygon) {
      return pointInPolygon([+location.lng, +location.lat], polygon.geometry.coordinates[0]);
    }

    function handleClickEvent(event) {
      const location = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      const intersectingPolygons = allPolygons.filter((polygon) => polygonIsIntersecting(location, polygon));
      if (!intersectingPolygons.length) return;

      const infoWindowHtml = document.createElement('div');

      infoWindowHtml.innerHTML = polygonsInfo(intersectingPolygons);
      infowindow.setContent(infoWindowHtml); // show the html variable in the infowindow
      infowindow.setPosition(location); // anchor the infowindow at the marker
      // move the infowindow up slightly to the top of the marker icon
      infowindow.setOptions({ pixelOffset: new google.maps.Size(0, 0) });
      infowindow.open(googleMap);
    }

    function newDataLayer(dataRaw, type, layerStatus, cnd) {
      const data = JSON.parse(JSON.stringify(dataRaw));
      const newLayer = new google.maps.Data();
      const colourType = type.slice(0, -1).toLowerCase(); // e.g. 'Garages' => 'garage'
      const shapeConstant = CONSTANTS[colourType][layerStatus.toLowerCase()];
      const { colour, index } = cnd ? shapeConstant[cnd] : shapeConstant;
      // if it has a C&D type, we go a level deeper to get the colour & z-index

      newLayer.setStyle(() => ({
        fillColor: colour,
        fillOpacity: 0.2,
        strokeColor: colour,
        strokeWidth: 0,
        strokeOpacity: 0.7,
        clickable: false,
        zIndex: index,
      }));

      const layerGeo = {
        type: 'FeatureCollection',
        name: type,
        features: data
          .filter((zone) => {
            // only matching statuses
            const { status } = getZoneAttribtutes(zone);
            return status.toUpperCase() === layerStatus;
          })
          .filter((zone) => {
            const { type, status, collectionType } = getZoneAttribtutes(zone);
            // apply toggleable 'type' filters (driver & garage)
            if (type === 'driver' && !includes(driverFilters, status)) return null;
            if (type === 'garage') {
              if (garageFilters && !includes(garageFilters, status)) return null;

              if (cnd === CUSTOMER_CND) {
                // Don't filter out any garage for customer C&D, as we need to show a circle for each
                if (cndTypeFilters && !includes(cndTypeFilters, cnd)) {
                  return null;
                }
              } else if (
                // apply toggleable C&D filter
                (cndTypeFilters && !includes(cndTypeFilters, collectionType)) ||
                // filter for only matching C&D types
                (cnd && cnd !== collectionType)
              ) {
                return null;
              }
            }
            return zone;
          })
          .map((zone) => {
            const coordsArr = zone.geoJson.geometry.coordinates[0];
            const coordsArrLength = coordsArr.length;
            /**
             * If first & last point of the polygon don't match and Gmaps can't
             * close the polygon, manually add first point to end of co-ords array
             */
            if (coordsArr[0] !== coordsArr[coordsArrLength - 1]) {
              zone.geoJson.geometry.coordinates[0].push(coordsArr[0]);
            }

            if (cnd === CUSTOMER_CND)
              zone.geoJson.geometry.coordinates = createCircleGeodata(zone).coordinates;

            const featureGeo = getFeatureGeo(zone);
            allPolygons.push(featureGeo);
            return featureGeo;
          }),
      };

      newLayer.setMap(googleMap);
      newLayer.addGeoJson(layerGeo);
      // newLayer.addListener('click', (event) => handleClickEvent(event));
    }

    if (garageShapes && driverShapes && districts) {
      mapInit();
      newDataLayer(garageShapes, 'Garages', 'ACTIVE', CUSTOMER_CND);
      newDataLayer(garageShapes, 'Garages', 'ACTIVE', GARAGE_CND);
      // newDataLayer(garageShapes, 'Garages', 'ACTIVE', 'Fixter');
      newDataLayer(garageShapes, 'Garages', 'DELETED');
      newDataLayer(garageShapes, 'Garages', 'SUSPENDED');
      newDataLayer(driverShapes, 'Drivers', 'ACTIVE');
      newDataLayer(driverShapes, 'Drivers', 'SUSPENDED');

      if (toggledDistricts) {
        const { colour, index } = CONSTANTS['zone-sectors'];
        const districtsLayer = new google.maps.Data();
        districtsLayer.setStyle(() => ({
          fillColor: colour,
          fillOpacity: 0.1,
          strokeWeight: 1,
          zIndex: index,
          clickable: false,
        }));
        districtsLayer.addGeoJson(districts);
        districtsLayer.setMap(googleMap);
      }

      if (toggledMarketingDistricts) {
        const { colour, index } = CONSTANTS['marketing-districts'];
        const districtsLayer = new google.maps.Data();
        districtsLayer.setStyle(() => ({
          fillColor: colour,
          fillOpacity: 0.8,
          strokeWeight: 2,
          zIndex: index,
          clickable: false,
        }));
        districtsLayer.addGeoJson(marketingDistricts);
        districtsLayer.setMap(googleMap);
      }

      googleMap.addListener('click', (event) => handleClickEvent(event));

      if (garageLocations) {
        garageLocations
          .filter(({ status, collectionType, lat, lng, name }) => {
            if (garageFilters && !includes(garageFilters, status)) return false;
            if (cndTypeFilters && !includes(cndTypeFilters, collectionType)) return false;
            if (!Number(lat) || !Number(lng)) {
              console.log('Garage is missing lat/long', { lat, lng, name });
              return false;
            }
            return true;
          })
          .forEach(({ lat, lng, name, collectionType, status, garageId }) => {
            const garageLocation = new google.maps.Marker({
              position: { lat, lng },
              title: name,
              zIndex: 101,
            });

            garageLocation.addListener('click', function () {
              console.log('Open garage tooltip', name);

              const location = { lat, lng };
              const infoWindowHtml = document.createElement('div');

              infoWindowHtml.innerHTML = polygonInfo({
                lat,
                lng,
                name,
                collectionType,
                status,
                garageId,
                type: 'garage',
              });
              infowindow.setContent(infoWindowHtml); // show the html variable in the infowindow
              infowindow.setPosition(location); // anchor the infowindow at the marker
              // move the infowindow up slightly to the top of the marker icon
              infowindow.setOptions({ pixelOffset: new google.maps.Size(0, 0) });

              infowindow.open(googleMap);
            });
            garageLocation.setMap(googleMap);
          });
      }

      if (markerPosition) {
        const marker = new google.maps.Marker({
          animation: google.maps.Animation.DROP,
          position: markerPosition,
          zIndex: 100,
        });
        marker.setMap(googleMap);
      }

      return () => {
        googleMap = null;
        return null;
      };
    }
  }, [
    garageShapes,
    garageFilters,
    driverFilters,
    toggledDistricts,
    cndTypeFilters,
    mapCentre,
    zoomLevel,
    driverShapes,
    districts,
    mapInit,
    markerPosition,
  ]);

  const saveZoomAndCenter = () => {
    setZoomLevel(googleMap.getZoom());
    setMapCentre(googleMap.getCenter());
  };

  const updateFilters = (status, type) => {
    saveZoomAndCenter();
    let dataToFilter, filterStateFunction;
    switch (type) {
      case 'garages':
        dataToFilter = garageFilters;
        filterStateFunction = (data) => setGarageFilters(data);
        break;
      case 'drivers':
        dataToFilter = driverFilters;
        filterStateFunction = (data) => setDriverFilters(data);
        break;
      case 'cnd':
        dataToFilter = cndTypeFilters;
        filterStateFunction = (data) => setCndTypeFilters(data);
        break;
      default:
        break;
    }

    if (includes(dataToFilter, status)) {
      filterStateFunction(dataToFilter.filter((f) => f !== status));
    } else {
      filterStateFunction(dataToFilter.concat(status));
    }
  };

  const showHideDistricts = () => {
    saveZoomAndCenter();
    toggleDistricts(!toggledDistricts);
  };

  const toggleAddZoneForm = () => {
    setShowForm(!showForm);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.click();
    return a;
  };

  const fetchOutbounds = (coverageThreshold) => {
    setLoadOutbounds(true);

    return getCoveredOutbounds(coverageThreshold)
      .then((res) => {
        setLoadOutbounds(false);
        return res;
      })
      .catch(() => {
        setLoadOutbounds(false);
        message.error(
          'There was an error trying to download the outbounds. Please try again or contact the dev team.'
        );
      });
  };

  const downloadOutbounds = (coverageThreshold) => {
    if (loadOutbounds) return [];
    return fetchOutbounds(coverageThreshold)
      .then((res) => {
        const blob = new Blob([res], { type: 'text/csv' });
        const fileName = `outbounds_${moment().format('YYYY-MM-DD')}.csv`;

        downloadBlob(blob, fileName);
        return res;
      })
      .catch(() => {
        message.error(
          'There was an error trying to download the outbounds. Please try again or contact the dev team.'
        );
      });
  };

  function marketingCSVtoJSON(csv) {
    const lines = csv.split('\n');
    const result = [];
    // NOTE: If your columns contain commas in their values, you'll need
    // to deal with those before doing the next step
    // (you might convert them to &&& or something, then covert them back later)
    // jsfiddle showing the issue https://jsfiddle.net/
    const headers = lines[0].split(',').map((i) => i.replaceAll('"', ''));
    for (let i = 1; i < lines.length; i++) {
      const obj = {};
      const currentline = lines[i].split(',');

      for (let j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentline[j].replaceAll('"', '');
      }

      result.push(obj);
    }

    return result;
  }

  const showMarketingAreas = () => {
    saveZoomAndCenter();
    fetchOutbounds().then((res) => {
      const marketingOutbounds = marketingCSVtoJSON(res);
      const marketingOutboundsMap = marketingOutbounds.reduce((acc, i) => {
        acc[i.outbound] = true;
        return acc;
      }, {});

      const newDistricts = { ...districts };
      newDistricts.features = newDistricts.features.filter((f) => !marketingOutboundsMap[f.properties.name]);

      setMarketingDistricts(newDistricts);

      toggleMarketingDistricts(!toggledMarketingDistricts);
    });
  };

  const createNewDriverArea = (newZone) =>
    createDriverZone(newZone)
      .then((zone) => {
        toggleAddZoneForm();
        message.success('Driver zone successfully created.');
        return history.push(`/${zone.ownerId}`);
      })
      .catch(() =>
        message.error(
          'There was an error trying to create the zone. Please try again or contact the dev team.'
        )
      );

  const resetForm = () => {
    toggleAddZoneForm();
  };

  const postcodeSearch = async (postcode) => {
    setFetchingPostcode(true);
    const match = /^[a-zA-Z]{1,2}([0-9]{1,2}|[0-9][a-zA-Z])\s*[0-9][a-zA-Z]{2}$/;
    const isValid = match.test(postcode.replace(/\W/g, ''));
    if (!isValid) {
      setFetchingPostcode(false);
      return message.error('Please enter a valid postcode');
    }
    const { latitude: lat, longitude: lng } = (await api.get(`/postcodes/${postcode}`)).data;

    setMarkerPosition({ lat, lng });
    setZoomLevel(19);
    setMapCentre({ lat, lng });
    setFetchingPostcode(false);
  };

  const Loading = () => <Spin indicator={<LogoSpinner />} />;

  const garageStatusFiltersDisabled = !cndTypeFilters.length;
  return (
    <Container>
      {!(garageShapes && driverShapes && districts) ? (
        <Loading />
      ) : (
        <>
          <Header>
            <ToggleGroup>
              <b>Garages Zones</b>
              <ToggleButton
                className="active-garages"
                onClick={() => updateFilters('ACTIVE', 'garages')}
                disabled={garageStatusFiltersDisabled}
                defaultChecked
              >
                Active
              </ToggleButton>
              <ToggleButton
                className="suspended-garages"
                onClick={() => updateFilters('SUSPENDED', 'garages')}
                disabled={garageStatusFiltersDisabled}
              >
                Suspended
              </ToggleButton>
              <ToggleButton
                className="deleted-garages"
                onClick={() => updateFilters('DELETED', 'garages')}
                disabled={garageStatusFiltersDisabled}
              >
                Deleted
              </ToggleButton>
              <ToggleButton
                className="customer-cnd"
                onClick={() => updateFilters(CUSTOMER_CND, 'cnd')}
                defaultChecked
              >
                Customer C&D
              </ToggleButton>
              <ToggleButton
                className="garage-cnd"
                onClick={() => updateFilters(GARAGE_CND, 'cnd')}
                defaultChecked
              >
                Garage C&D
              </ToggleButton>
              <ToggleButton className="fixter-cnd" onClick={() => updateFilters('Fixter', 'cnd')}>
                Fixter C&D
              </ToggleButton>
            </ToggleGroup>

            <ToggleGroup>
              <b>Driver Zones</b>
              <ToggleButton
                className="active-drivers"
                onClick={() => updateFilters('ACTIVE', 'drivers')}
                defaultChecked
              >
                Active
              </ToggleButton>
              <ToggleButton
                className="suspended-drivers"
                onClick={() => updateFilters('SUSPENDED', 'drivers')}
              >
                Suspended
              </ToggleButton>
            </ToggleGroup>

            <ToggleGroup>
              <b>Other</b>
              <ToggleButton className="districts" onClick={() => showHideDistricts()}>
                Zone Sectors
              </ToggleButton>
              <ToggleButton className="marketing-districts" onClick={() => showMarketingAreas()}>
                Marketing areas
              </ToggleButton>
            </ToggleGroup>
            <ToggleGroup style={{ paddingRight: '10px' }}>
              <b>Customer C&D Radius ( miles ) </b>
              <input
                style={{ margin: '10px 18px 12px 2px' }}
                type="number"
                value={customerRadiusMiles}
                onChange={(e) => setCustomerRadiusMiles(e.target.value)}
              />
            </ToggleGroup>
          </Header>
          <MapView>
            <PostcodeSearchContainer>
              <Search
                size="large"
                loading={fetchingPostcode}
                onSearch={(pc) => postcodeSearch(pc)}
                placeholder="Search by postcode"
              />
            </PostcodeSearchContainer>
            <MapMount ref={mapRef} />
          </MapView>
          <MapFooter>
            <DownloadOutboundsButton onClick={() => downloadOutbounds(80)}>
              {loadOutbounds && <Spin indicator={<LogoSpinner />} />}
              {!loadOutbounds && <Icon type="download" />}
              Export only outbounds with more then 80% coverage
            </DownloadOutboundsButton>

            <DownloadOutboundsButton onClick={() => downloadOutbounds(1)}>
              {loadOutbounds && <Spin indicator={<LogoSpinner />} />}
              {!loadOutbounds && <Icon type="download" />}
              Export all covered outbounds
            </DownloadOutboundsButton>

            <NewZoneButton onClick={() => toggleAddZoneForm()}>Add new driver zone</NewZoneButton>
          </MapFooter>

          <If condition={showForm}>
            <ContainFormRight>
              <ZoneEditorForm onSuccess={createNewDriverArea} onReset={resetForm} editing={false} />
            </ContainFormRight>
          </If>
        </>
      )}
    </Container>
  );
};

export default withRouter(CoverageZoneMap);
